import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, ChevronDown, Shield, Globe, User, LogOut, LayoutDashboard } from 'lucide-react';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const { user, logout } = useAuth();
  const { lang, toggleLang, t } = useLanguage();
  const location = useLocation();
  const dropdownRef = useRef(null);

  const isPortalPage = location.pathname.startsWith('/portal') || location.pathname.startsWith('/admin');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isTransparentPage = location.pathname === '/';
  const navBg = scrolled || !isTransparentPage
    ? 'bg-white shadow-md border-b border-gray-100'
    : 'bg-transparent';
  const textColor = (scrolled || !isTransparentPage) ? 'text-[#0B1F3A]' : 'text-white';
  const logoColor = (scrolled || !isTransparentPage) ? 'text-[#0B1F3A]' : 'text-white';

  const navLinks = [
    { to: '/', label: t('navHome') },
    { to: '/services', label: t('navServices'), dropdown: true },
    { to: '/who-we-help', label: t('navWhoWeHelp') },
    { to: '/blog', label: t('navBlog') },
    { to: '/about', label: t('navAbout') },
    { to: '/contact', label: t('navContact') },
  ];

  const serviceLinks = [
    { to: '/services#individual', label: 'Individual Tax Returns' },
    { to: '/services#business', label: 'Business Tax Returns' },
    { to: '/services#self-employed', label: 'Self-Employed / 1099' },
    { to: '/services#multi-state', label: 'Multi-State Filing' },
    { to: '/services#planning', label: 'Tax Planning' },
    { to: '/services#audit', label: 'IRS Audit Representation' },
  ];

  if (isPortalPage) {
    // Portal pages have their own header, don't render navbar
    return null;
  }

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-18 py-3">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md" style={{ background: '#D4AF37' }}>
              <Shield size={22} className="text-[#0B1F3A]" />
            </div>
            <div>
              <div className={`font-heading font-bold text-base leading-none ${logoColor}`}>MaTaxPreparer</div>
              <div className={`text-[10px] tracking-widest uppercase opacity-80 ${logoColor}`}>LLC · Tax Professionals</div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.to} className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setServicesOpen(!servicesOpen)}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 hover:bg-black/5 ${textColor}`}
                    data-testid="services-dropdown-btn"
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-floating border border-gray-100 py-2 z-50 animate-fadeIn">
                      {serviceLinks.map((sl) => (
                        <Link
                          key={sl.to}
                          to={sl.to}
                          onClick={() => setServicesOpen(false)}
                          className="block px-5 py-3 text-sm text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0B1F3A] transition-colors duration-150"
                        >
                          {sl.label}
                        </Link>
                      ))}
                      <div className="border-t border-gray-100 mt-2 pt-2">
                        <Link
                          to="/services"
                          onClick={() => setServicesOpen(false)}
                          className="block px-5 py-3 text-sm font-semibold text-[#0B1F3A] hover:bg-[#F8FAFC]"
                        >
                          View All Services →
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 hover:bg-black/5 ${textColor} ${location.pathname === link.to ? 'font-semibold' : ''}`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleLang}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-colors duration-200 ${scrolled || !isTransparentPage ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-white/10 text-white hover:bg-white/20'}`}
              data-testid="language-toggle"
            >
              <Globe size={14} />
              {lang === 'en' ? 'ES' : 'EN'}
            </button>

            {user ? (
              <Link
                to={user.role === 'admin' ? '/admin' : '/portal'}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 bg-[#0B1F3A] text-white hover:bg-[#1a3a5c]"
                data-testid="portal-link"
              >
                <LayoutDashboard size={16} />
                {user.role === 'admin' ? 'Admin' : t('navPortal')}
              </Link>
            ) : (
              <Link
                to="/login"
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${scrolled || !isTransparentPage ? 'text-[#0B1F3A] hover:bg-gray-100' : 'text-white hover:bg-white/10'}`}
                data-testid="login-link"
              >
                <User size={16} />
                {t('navLogin')}
              </Link>
            )}

            <Link
              to="/book-appointment"
              className="btn-gold text-sm px-5 py-2.5 font-semibold"
              data-testid="book-appointment-btn"
            >
              {t('bookAppt')}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors duration-200 ${textColor}`}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg animate-fadeIn" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block px-4 py-3 rounded-lg text-[#0B1F3A] font-medium hover:bg-[#F8FAFC] transition-colors duration-150"
              >
                {link.label}
              </Link>
            ))}
            <div className="border-t border-gray-100 pt-3 mt-3 space-y-2">
              <Link to="/book-appointment" className="block px-4 py-3 rounded-lg bg-[#D4AF37] text-[#0B1F3A] font-semibold text-center">
                {t('bookAppt')}
              </Link>
              {user ? (
                <>
                  <Link to={user.role === 'admin' ? '/admin' : '/portal'} className="block px-4 py-3 rounded-lg bg-[#0B1F3A] text-white font-semibold text-center">
                    {user.role === 'admin' ? 'Admin Dashboard' : 'My Portal'}
                  </Link>
                  <button onClick={logout} className="w-full px-4 py-3 rounded-lg border border-gray-200 text-gray-600 font-medium">
                    Logout
                  </button>
                </>
              ) : (
                <Link to="/login" className="block px-4 py-3 rounded-lg border border-gray-200 text-[#0B1F3A] font-semibold text-center">
                  {t('navLogin')}
                </Link>
              )}
              <button onClick={toggleLang} className="w-full px-4 py-3 rounded-lg bg-gray-100 text-gray-600 font-medium flex items-center justify-center gap-2">
                <Globe size={16} />
                Switch to {lang === 'en' ? 'Español' : 'English'}
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
